import { createEmailTemplate, createLandingPage } from '../../../API/PhishingAPI';
import { associateSimulationPage } from '../../../shared/simulationTemplates';

export const socialTemplates = [
  { id: 'facebook', name: 'Facebook', icon: 'user' },
  { id: 'instagram', name: 'Instagram', icon: 'camera' },
  { id: 'twitter', name: 'Twitter (X)', icon: 'chat' },
  { id: 'linkedin', name: 'LinkedIn', icon: 'portfolio' },
  { id: 'reddit', name: 'Reddit', icon: 'sun' },
  { id: 'pinterest', name: 'Pinterest', icon: 'map-marker' },
  { id: 'whatsapp', name: 'WhatsApp', icon: 'call' },
  { id: 'telegram', name: 'Telegram', icon: 'chat' }
];

export function createBlankTemplate(t) {
  const name = t('template_gallery.new_template');
  const document = content => `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"></head><body style="font-family:Arial,sans-serif;padding:32px;color:#152037">${content}</body></html>`;
  return {
    id: 'new-set', name,
    page: { name, capture_credentials: true, capture_passwords: false, redirect_url: '',
      html: document(`<h1>${escapeHtml(name)}</h1><form method="POST" action=""><label for="email">${escapeHtml(t('template_gallery.account_email'))}</label><p><input type="email" name="email" id="email" required></p><button type="submit">${escapeHtml(t('template_gallery.continue'))}</button></form>`) },
    message: { name, subject: t('template_gallery.new_subject'), text: '', attachments: [],
      html: document(`<p>${escapeHtml(t('template_gallery.greeting'))} {{.FirstName}},</p><p><a href="{{.URL}}">${escapeHtml(t('template_gallery.continue'))}</a></p>{{.Tracker}}`) }
  };
}

const escapeHtml = value => String(value).replace(/[&<>"']/g, character => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
}[character]));

export function createSocialTemplate(model, t) {
  const name = model.name;
  const description = t(`template_gallery.scenarios.${model.id}`);
  const action = t('template_gallery.verify');
  const document = body => `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><title>${escapeHtml(name)}</title></head><body style="margin:0;padding:32px 16px;background:#f4f6fb;font-family:Arial,sans-serif;color:#182238"><div style="max-width:480px;margin:24px auto;padding:32px;background:#fff;border:1px solid #dce3ef;border-radius:12px"><h1 style="font-size:26px;color:#3154ec;margin:0 0 24px">${escapeHtml(name)}</h1>${body}</div></body></html>`;
  const buttonStyle = 'display:inline-block;padding:14px 24px;background:#3154ec;color:#fff;border:0;border-radius:6px;font-size:16px;text-decoration:none';
  return {
    id: `social-${model.id}`, name, description, category: t('template_gallery.social'),
    source: { kind: 'builtin', slug: model.id }, icon: model.icon,
    page: {
      name, capture_credentials: true, capture_passwords: false, redirect_url: '',
      html: document(`<h2 style="font-size:20px">${escapeHtml(action)}</h2><p style="line-height:1.6">${escapeHtml(description)}</p><form method="POST" action=""><label for="email">${escapeHtml(t('template_gallery.account_email'))}</label><input id="email" name="email" type="email" required autocomplete="off" style="display:block;box-sizing:border-box;width:100%;margin:12px 0 20px;padding:14px;border:1px solid #cbd5e1;border-radius:6px"><button type="submit" style="${buttonStyle}">${escapeHtml(action)}</button></form>`)
    },
    message: {
      name, subject: t('template_gallery.subject', { brand: name }), attachments: [],
      text: `${name}\n\n${description}\n\n${action}: {{.URL}}`,
      html: document(`<p>${escapeHtml(t('template_gallery.greeting'))} {{.FirstName}},</p><p style="line-height:1.6">${escapeHtml(description)}</p><p style="margin:28px 0"><a href="{{.URL}}" style="${buttonStyle}">${escapeHtml(action)}</a></p>{{.Tracker}}`)
    }
  };
}

function normalizedName(name = '') {
  return name.toLowerCase().replace(/\b(landing\s*page|email\s*template|email|message|landing)\b/g, '')
    .replace(/[^a-z0-9\u00c0-\u024f]/g, '');
}

// Only pair unambiguous names or explicit set IDs, never a broad brand match.
export function pairLibraryTemplates(pages, messages) {
  const key = item => item.template_set_id ? `set:${item.template_set_id}` : `name:${normalizedName(item.name)}`;
  const pageGroups = new Map(), messageGroups = new Map();
  pages.forEach(page => pageGroups.set(key(page), [...(pageGroups.get(key(page)) || []), page]));
  messages.forEach(message => messageGroups.set(key(message), [...(messageGroups.get(key(message)) || []), message]));
  const usedMessages = new Set();
  const sets = pages.map(page => {
    const matches = messageGroups.get(key(page)) || [];
    const message = pageGroups.get(key(page)).length === 1 && matches.length === 1 ? matches[0] : null;
    if (message) usedMessages.add(message);
    const category = page.category || message?.category;
    return { id: `library-page-${page.id}`, name: page.name, description: page.description || message?.description,
      category: category || suggestedCategory(page.name), categorySuggested: !category && !!suggestedCategory(page.name),
      source: { kind: 'gophish-library', pageId: page.id, messageId: message?.id },
      page, message, library: true, icon: 'browser' };
  });
  messages.filter(message => !usedMessages.has(message)).forEach(message => sets.push({
    id: `library-message-${message.id}`, name: message.name, description: message.description,
    category: message.category || suggestedCategory(message.name), categorySuggested: !message.category && !!suggestedCategory(message.name),
    source: { kind: 'gophish-library', messageId: message.id },
    message, page: null, library: true, icon: 'mail'
  }));
  return sets;
}

function suggestedCategory(name = '') {
  if (!name.includes('/')) return '';
  return name.split('/')[0].replace(/[-_]/g, ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

export function copyTemplateSet(set) {
  const copy = item => {
    if (!item) return null;
    const { id, modified_date, template_set_id, description, ...content } = item;
    return JSON.parse(JSON.stringify(set.existing ? { ...content, id } : content));
  };
  return { ...set, page: copy(set.page), message: copy(set.message) };
}

// Retain confirmed writes if the second resource fails. A retry in the same
// dialog only writes the missing component.
export async function saveTemplateSet(set, saved, onSaved) {
  if (!set.page || !set.message) throw new Error('A simulation template requires both a landing page and an email');
  if (set.page && !saved.page) {
    const page = await createLandingPage({ ...set.page, name: set.name });
    saved.page = page;
    onSaved('page', page);
  }
  if (set.message && !saved.message) {
    // Text-only EMLs still need a visible HTML alternative because the local
    // association metadata lives in the HTML document head.
    const html = set.message.html?.trim() ? set.message.html : set.message.text
      ? `<html><head><meta charset="utf-8"></head><body><div style="white-space:pre-wrap;font-family:Arial,sans-serif">${escapeHtml(set.message.text)}</div></body></html>` : '';
    // Sender identity belongs to the sending profile, including for imported
    // templates that carried an optional envelope sender in the source.
    const message = await createEmailTemplate({ ...set.message, name: set.name, envelope_sender: '',
      html: associateSimulationPage(html, saved.page.id, {
        description: set.description, category: set.category, source: set.source
      }) });
    saved.message = message;
    onSaved('message', message);
  }
  return saved;
}
